/**
 * HTTP client for the staff (POS) API.
 * Behind Cloudflare Access the edge injects `Cf-Access-Jwt-Assertion` on same-origin requests,
 * so the browser sends nothing extra; in development the dev bypass token is attached instead.
 */
import type {
  ApiError,
  Brand,
  LunchPack,
  Mall,
  PosScanRequest,
  PosScanResult,
  PosTransactionRequest,
  PosTransactionResult,
} from "./api-types";

const DEV_TOKEN_KEY = "gp.scanner.staffDevToken";

/** API base without a trailing slash; `VITE_API_BASE` (e.g. http://localhost:8787/v1) or same-origin `/v1`. */
export const apiBase: string = (import.meta.env.VITE_API_BASE ?? "/v1").replace(/\/+$/, "");

export function getDevToken(): string | null {
  return localStorage.getItem(DEV_TOKEN_KEY);
}

export function setDevToken(token: string | null): void {
  if (token) localStorage.setItem(DEV_TOKEN_KEY, token);
  else localStorage.removeItem(DEV_TOKEN_KEY);
}

/** Non-2xx answer from the API. `retryable` is true for 5xx/408/429 where resending is safe. */
export class ApiHttpError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiError,
  ) {
    super(`${status} ${body.code}: ${body.message}`);
    this.name = "ApiHttpError";
  }
  get retryable(): boolean {
    return this.status >= 500 || this.status === 408;
  }
}

/** The request never reached the API (offline, DNS, CORS, aborted). Always safe to retry with the same key. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = "NetworkError";
  }
}

function isApiError(value: unknown): value is ApiError {
  return typeof value === "object" && value !== null && typeof (value as ApiError).code === "string";
}

async function request<T>(method: string, path: string, body?: unknown, extraHeaders: Record<string, string> = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json", ...extraHeaders };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const devToken = getDevToken();
  if (devToken) headers["X-Staff-Dev-Token"] = devToken;

  let response: Response;
  try {
    response = await fetch(`${apiBase}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: "same-origin",
    });
  } catch (error) {
    throw new NetworkError(error);
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }
  if (!response.ok) {
    throw new ApiHttpError(response.status, isApiError(parsed) ? parsed : { code: `http_${response.status}`, message: response.statusText || "Request failed" });
  }
  if (parsed === null) throw new ApiHttpError(response.status, { code: "invalid_response", message: "Expected a JSON body." });
  return parsed as T;
}

export const staffApi = {
  listBrands: () => request<{ brands: Brand[] }>("GET", "/brands").then((r) => r.brands),
  listMalls: () => request<{ malls: Mall[] }>("GET", "/malls").then((r) => r.malls),
  listLunchPacks: () => request<{ packs: LunchPack[] }>("GET", "/lunch-packs").then((r) => r.packs),
  scan: (body: PosScanRequest) => request<PosScanResult>("POST", "/pos/scans", body),
  postTransaction: (body: PosTransactionRequest, idempotencyKey: string) =>
    request<PosTransactionResult>("POST", "/pos/transactions", body, { "Idempotency-Key": idempotencyKey }),
  setIsic: (memberId: string, verified: boolean) =>
    request<void>("PUT", `/pos/members/${encodeURIComponent(memberId)}/isic`, { verified }),
};
